<script setup lang="ts">
import type { Ref } from "vue";
import { computed, inject, ref } from "vue";
import VAppLink from "@magnit/core/src/components/VAppLink/VAppLink.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VLabel from "@magnit/core/src/components/VLabel/VLabel.vue";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VStepper from "@magnit/core/src/components/VStepper/VStepper.vue";
import VFavoriteButton from "@magnit/core/src/containers/VFavoriteButton/VFavoriteButton.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import StarIcon from "@magnit/icons/src/assets/svg/icons/base/16-system-star.svg";
import BonusIcon from "@magnit/icons/src/assets/icons/loyalty-bonus-16-color.svg";
import MagnetIcon from "@magnit/icons/src/assets/icons/loyalty-magnitik-16-color.svg";
import BasketIcon from "@magnit/icons/src/assets/svg/icons/base/24-outline-basket.svg";
import useLoadProductImage from "../composables/useLoadProductImage";
import VProductAdultImage from "../components/VProductAdultImage.vue";
import VCatalogBadge from "../components/VCatalogBadge.vue";
import { formatPrice } from "../utils/converter";
import {
  type IProductPreviewProps,
  type IProductPreviewEmits,
  type IBasketAction,
  IPromoProductProgressiveDiscountType,
} from "@magnit/unit-catalog/src/unit-catalog.types";
import { provideKeys } from "@magnit/unit-catalog/src/unit-catalog.vars";
import { pluralRatingCount } from "../utils/plural";

const props = withDefaults(defineProps<IProductPreviewProps>(), {
  badges: () => [],
  theme: "base",
  basketQty: 0,
});
const emit = defineEmits<IProductPreviewEmits>();
const { imageSrc } = useLoadProductImage(props.image);

const adultApproved = inject<Ref<boolean>>(provideKeys.approved, ref(false));
const isDialogOpened = inject<Ref<boolean>>(
  provideKeys.isDialogOpened,
  ref(false),
);

const onAdultClick = () => {
  if (!adultApproved.value) {
    isDialogOpened.value = true;
  }
};
const displayedProgressive = computed(
  () =>
    props.progressiveDiscountVariants?.[
      props.progressiveDiscountVariants?.length - 1 || 0
    ],
);
const progressiveStringValues = computed(() => {
  if (!displayedProgressive.value) return null;
  switch (props.progressiveDiscountType) {
    case IPromoProductProgressiveDiscountType.BONUSES:
      return {
        start: `+ ${displayedProgressive.value?.value}`,
        end: "бонусами",
        icon: BonusIcon,
      };
    case IPromoProductProgressiveDiscountType.MAGNETS:
      return {
        start: `+ ${displayedProgressive.value?.value}`,
        end: "Магнитиками",
        icon: MagnetIcon,
      };
    case IPromoProductProgressiveDiscountType.CASHBACK:
      return {
        start: `+ ${displayedProgressive.value?.value}%`,
        end: "бонусами",
        icon: BonusIcon,
      };
    default:
      return null;
  }
});
const displaySellPercents = computed(
  () =>
    props.salePercent ||
    props.progressiveDiscountType ===
      IPromoProductProgressiveDiscountType.PERCENT,
);
const sellPercents = computed(
  () => props.salePercent || displayedProgressive.value?.label || "",
);
const displayPrice = computed(
  () =>
    props.price &&
    props.progressiveDiscountType !==
      IPromoProductProgressiveDiscountType.PRICE,
);
const displayProgressivePrice = computed(
  () =>
    props.progressiveDiscountType ===
      IPromoProductProgressiveDiscountType.PRICE && displayedProgressive?.value,
);
const displayLabels = computed(
  () => props.profit || props.badges?.length || props.badge,
);
const profitBadge = computed(() =>
  props.profit
    ? {
        labelSize: { xs: "xs", l: "s" } as const,
        badge: {
          text: `Экономия до ${formatPrice(props.profit)}` as string,
        },
      }
    : null,
);
const specialBadge = computed(() => ({
  labelSize: { xs: "xs", l: "s" } as const,
  badge: {
    text: props.badge as string,
    textColor: "var(--u-catalog-product-card-discount-color)",
    backgroundColor: "var(--u-catalog-product-card-discount-background)",
  },
}));
const salePercentBadge = computed(() => ({
  labelSize: { xs: "xs", l: "s" } as const,
  badge: {
    text: sellPercents.value,
    backgroundColor: "var(--u-catalog-product-card-discount-background)",
    textColor: "var(--u-catalog-product-card-discount-color)",
  },
}));

const commentsCount = computed(() =>
  props.ratings?.commentsCount
    ? `· ${props.ratings?.commentsCount} ${pluralRatingCount(props.ratings?.commentsCount)}`
    : "Нет отзывов",
);

const weightedLabel = computed(() => {
  if (!props.weighted?.isWeighted) {
    return "";
  }
  return `${formatPrice((props.weighted?.unitPrice ?? 0) / 100)} · ${props.weighted.unitLabel}`;
});
const showWeightedLabel = computed(
  () => inStock.value && (props.unit || weightedLabel.value),
);

const ratingScoreClass = computed(() => {
  if (!props.ratings?.rating || props.theme === "base") {
    return "";
  }
  return props.ratings.rating >= 4 ? "rating-score-high" : "rating-score-low";
});

const basketAvailable = computed(() => props.ecom && !props.isPromo);
const inStock = computed(() => !!props.quantity);
const displayBasketQty = computed(() => {
  if (props.weighted?.step) return props.basketQty / 1000;
  return props.basketQty;
});
const displayBasketUnit = computed(() => {
  if (!props.weighted?.isWeighted) return "шт";

  return "кг";
});
const basketStep = computed(() => {
  if (props.weighted?.isWeighted && props.weighted?.step) {
    return props.weighted.step;
  }

  return 1;
});

const maxQuanitity = computed(() => {
  if (!props.quantity) return 0;
  if (props.weighted?.isWeighted && props.weighted?.step) {
    return props.quantity / props.weighted.step;
  }

  return props.quantity;
});

const onFavoriteClick = () =>
  emit("favorite:set", props.id, !props.isInFavorites);

const onRatingClick = () => {
  if (!props.adult) {
    emit("rating:click", props.id);
  }
};
const onBasket = (action: IBasketAction) => {
  emit("basket", props.id, basketStep.value, action);
};
</script>

<template>
  <article class="unit-catalog-product-preview">
    <VAppLink
      :disabled="!adultApproved && adult"
      :to="link"
      :title="title"
      class="pl-hover-base"
    >
      <div class="unit-catalog-product-preview-gallery">
        <div class="unit-catalog-product-preview-image">
          <VProductAdultImage
            :blurred="!adultApproved && adult"
            @click="onAdultClick"
          >
            <img :src="imageSrc" :alt="title" loading="lazy" />
          </VProductAdultImage>

          <VFavoriteButton
            v-if="favoritesAvailable"
            :favorite="isInFavorites"
            class="unit-catalog-product-preview-favorite"
            :loading="favoritesLoading"
            @click="onFavoriteClick"
          />
          <div v-if="displayLabels" class="unit-catalog-product-preview-labels">
            <div class="unit-catalog-product-preview-labels__badges">
              <VLabel
                v-if="profitBadge"
                theme="discount"
                :size="profitBadge.labelSize"
              >
                <span v-html="profitBadge.badge.text"></span>
              </VLabel>
              <VCatalogBadge
                v-if="badge"
                v-bind="specialBadge"
                class="unit-catalog-product-preview-labels__badges-item unit-catalog-product-preview-labels__badges-item_special"
              />
              <VCatalogBadge
                v-for="badgesItem in badges"
                :key="badgesItem.text"
                class="unit-catalog-product-preview-labels__badges-item"
                :badge="badgesItem"
                :label-size="{ xs: 'xs', l: 's' }"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="unit-catalog-product-preview-description">
        <div class="unit-catalog-product-preview-text">
          <div
            v-if="displayedProgressive"
            class="unit-catalog-product-preview-progressive-variant"
          >
            <VText font="caption-large-regular">
              {{ displayedProgressive.countLabel }}
            </VText>
          </div>

          <div
            v-if="inStock || isPromo"
            class="unit-catalog-product-preview-prices unit-catalog-product-preview-prices_ordinary"
          >
            <div class="unit-catalog-product-preview-prices__top-row">
              <VText
                v-if="displayPrice"
                :font="{ xs: 'title-small', l: 'title-medium' }"
                class="unit-catalog-product-preview-prices__regular"
              >
                <span v-html="formatPrice(price!)"></span>
              </VText>
              <VCatalogBadge
                v-if="displaySellPercents"
                class="unit-catalog-product-preview__discount"
                v-bind="salePercentBadge"
              />
            </div>

            <VText
              v-if="oldPrice"
              class="unit-catalog-product-preview-prices__sale"
              color="secondary"
              :font="{
                xs: 'caption-small-regular-low',
                l: 'body-small-regular-low',
              }"
            >
              <span v-html="formatPrice(oldPrice)"></span>
            </VText>
          </div>

          <div
            v-if="displayProgressivePrice"
            class="unit-catalog-product-preview-prices"
          >
            <VText
              :font="'body-large-accent'"
              class="unit-catalog-product-preview-prices__regular"
            >
              <span
                v-html="formatPrice(displayedProgressive!.value / 100)"
              ></span>
            </VText>
          </div>
          <div
            v-if="progressiveStringValues"
            class="unit-catalog-product-preview-progressive-string"
          >
            <VText font="body-small-accent"
              >{{ progressiveStringValues.start }}
            </VText>
            <VIcon>
              <Component :is="progressiveStringValues.icon" />
            </VIcon>
            <VText font="caption-large-regular">{{
              progressiveStringValues.end
            }}</VText>
          </div>
          <VText
            v-if="title"
            tag="div"
            class="unit-catalog-product-preview-title"
          >
            {{ title }}
          </VText>
        </div>
        <div
          v-if="showWeightedLabel"
          class="unit-catalog-product-preview-unit-wrap"
        >
          <VText
            v-if="unit"
            class="unit-catalog-product-preview-unit-value"
            font="caption-large-regular-low"
          >
            {{ unit }}
          </VText>
          <VText
            v-if="weightedLabel"
            class="unit-catalog-product-preview-weighted"
            font="caption-large-regular-low"
            color="secondary"
          >
            <span v-html="weightedLabel" />
          </VText>
        </div>
        <div
          class="unit-catalog-product-preview-rating"
          @click.stop.prevent="onRatingClick"
        >
          <VIcon v-if="theme === 'base' && ratings?.rating">
            <StarIcon />
          </VIcon>
          <VText
            v-if="ratings && ratings.rating"
            class="unit-catalog-product-preview-rating-score"
            :class="ratingScoreClass"
            font="caption-large-regular-low"
            color="secondary"
          >
            {{ ratings.rating }}
          </VText>
          <VText
            class="unit-catalog-product-preview-rating-comments"
            font="caption-large-regular-low"
            color="secondary"
          >
            {{ commentsCount }}
          </VText>
        </div>

        <VText
          v-if="!inStock && !isPromo"
          tag="div"
          font="caption-small-accent-low"
          color="accent"
          class="unit-catalog-product-preview__not-in-stock"
        >
          Нет в наличии
        </VText>
      </div>
      <div
        v-if="basketAvailable"
        class="unit-catalog-product-preview__ecom"
        @click.stop.prevent
      >
        <VStepper
          v-if="basketQty && inStock"
          size="s"
          :value="displayBasketQty"
          :max="maxQuanitity"
          :unit="displayBasketUnit"
          :loading="basketLoading"
          @increment="onBasket('inc')"
          @decrement="onBasket('dec')"
          @delete="onBasket('del')"
        />
        <VButton
          v-else
          full-width
          :disabled="!inStock"
          icon-position="left"
          theme="secondary"
          size="s"
          @click.stop.prevent="onBasket('inc')"
        >
          <template #icon>
            <BasketIcon />
          </template>
          В корзину
        </VButton>
      </div>
    </VAppLink>
  </article>
</template>

<style lang="postcss" scoped>
.unit-catalog-product-preview {
  display: flex;
  flex-direction: column;
  height: 100%;

  &:deep(a) {
    display: flex;
    flex-direction: column;
    height: 100%;
    text-decoration: none;
    background: var(--pl-surface-primary-default);
    border-radius: var(--u-catalog-product-image-border-radius);
  }

  &-unit-wrap {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--pl-unit-x2);
    margin-top: var(--pl-unit-x2);
  }

  &__ecom {
    margin-top: var(--pl-unit-x1);
    flex-grow: 1;
    align-content: end;
  }

  &__not-in-stock {
    margin-top: var(--pl-unit-x1);
    margin-bottom: var(--pl-unit-x2);
  }
}

.unit-catalog-product-preview-gallery {
  position: relative;
  border-radius: var(--u-catalog-product-image-border-radius);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.unit-catalog-product-preview-image {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  border-radius: var(--u-catalog-product-image-border-radius);
  padding: var(--pl-unit-x2);
  background: var(--pl-surface-muted-default);
  transition: background-color 0.2s;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    pointer-events: none;
    border-radius: var(--u-catalog-product-image-inner-border-radius);
    transition: filter 0.2s;
    filter: brightness(0.96); /* Ибо много картинок с белым фоном */
  }
}

.unit-catalog-product-preview-description {
  padding: var(--pl-unit-x1) 6px var(--pl-unit-x2);

  @media (--pl-viewport-from-l) {
    padding: var(--pl-unit-x2) var(--pl-unit-x2) var(--pl-unit-x2);
  }
}

.unit-catalog-product-preview-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.unit-catalog-product-preview-prices {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 var(--pl-unit-x2);

  &_ordinary {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  }

  &__sale {
    position: relative;
    display: inline-block;
    overflow: hidden;

    &::after {
      /* Спасибо Олегу за эту скруглённую черту :-) */
      content: "";
      position: absolute;
      width: 300%;
      height: 170%;
      border: 1px solid var(--pl-text-accent);
      border-radius: 50%;
      left: -20%;
      top: 18%;
    }
  }

  &__top-row {
    display: flex;
    align-items: center;
  }
}

.unit-catalog-product-preview__discount {
  display: inline-flex;
  margin-top: 2px;
  margin-bottom: 2px;

  .unit-catalog-product-preview-prices__regular + & {
    margin-left: 4px;
  }
}

.unit-catalog-product-preview-progressive-variant {
  display: inline-flex;
  min-width: var(--pl-unit-x5);
  justify-content: center;
  align-items: center;
  padding: 0 var(--pl-unit-x1);
  border-radius: var(--pl-unit-x1);
  border: 1px solid var(--pl-border-secondary-pressed);
  background: var(--pl-surface-muted-default);
}

.unit-catalog-product-preview-progressive-string {
  display: inline-flex;
  vertical-align: middle;
  align-items: center;
  padding-top: var(--pl-unit-x1);
  gap: var(--pl-unit-x1);
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;

  & > span {
    white-space: nowrap;
  }
}

.unit-catalog-product-preview-title {
  margin-top: var(--pl-unit-x1);
  display: -webkit-box; /* stylelint-disable-line */
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font: var(--u-catalog-product-card-title);

  @media (--pl-viewport-from-l) {
    -webkit-line-clamp: 3;
  }
}

.unit-catalog-product-preview-rating {
  display: none;
  column-gap: var(--pl-unit-x1);
  margin-top: var(--pl-unit-x1);

  &-score {
    &.rating-score-high {
      color: var(--u-catalog-product-rating-score-color-high);
    }

    &.rating-score-low {
      color: var(--u-catalog-product-rating-score-color-low);
    }
  }
}

.unit-catalog-product-preview.show-ratings
  .unit-catalog-product-preview-rating {
  display: flex;
}

.unit-catalog-product-preview-labels {
  position: absolute;
  bottom: 0;
  left: 0;

  &__badges {
    display: flex;
    flex-wrap: wrap;
    padding: var(--pl-unit-x2);
    overflow: hidden;
    gap: var(--pl-unit-x1);
  }
}

.unit-catalog-product-preview .unit-catalog-product-preview-favorite {
  position: absolute;
  top: var(--pl-unit-x3);
  right: var(--pl-unit-x3);
}
</style>
